import { Injectable } from '@angular/core';
import { AppSettingsService } from './app-settings.service';
import { map, shareReplay } from 'rxjs/operators';
import { HttpClient } from '@angular/common/http';
import { Observable, from, firstValueFrom, Subject } from 'rxjs';
import { PrioritizesScanningBarcodeEnum, SOLockAutomaticActions, SOLockSettings, SOLockTypes, SystemSetting, SystemSettingsEnum, UiControlToFocusOnAfterAddingItemEnum } from 'src/app/shared/models/system-settings';
import { DexieDbProvider } from '@shared';
import { AuthService } from '@auth';
import moment from 'moment';
import { QueryStringHelper } from 'src/app/shared/helpers/query-string.helper';
import { PagedList } from 'src/app/shared/models/paged-list';
import { InventoryManagementMethod } from 'src/app/shared/models/Inventory-management-method';
import { OrganizationProfile, OrdersSetting, PurchaseInvoicesSetting, SalesInvoicesSetting, ProductsSetting, GlobalSettings, ModulesActivation, EInvoiceKsaConfiguration } from 'src/app/system/models/system-settings';


@Injectable({
    providedIn: 'root'
})
export class SystemSettingsService {

    baseUrl = '';
    modulesActivationSettingsChanged$: Subject<ModulesActivation> = new Subject();

    private offlineModeEnabled$?: Observable<boolean>;
    private offlineModeExcludedUsers$?: Observable<string>;
    private hideSubTotal$?: Observable<boolean>;
    private systemStartDate$?: Observable<string>;
    private financialYearEndDate$?: Observable<string>;
    private allowSellingBySerialNumber$?: Observable<boolean>;
    private checkSerialOwner$?: Observable<boolean>;
    private checkSerialAvailability$?: Observable<boolean>;
    private checkSerialFulfillment$?: Observable<boolean>;
    private inventoryManagementMethod$?: Observable<InventoryManagementMethod>;
    private hideBalanceAndBatchDetails$?: Observable<boolean>;
    private uiControlToFocusOnAfterAddingItem$?: Observable<string>;
    private prioritizesScanningBarcode$?: Observable<PrioritizesScanningBarcodeEnum>;
    private preventSellingZeroBalanceStockItems$?: Observable<boolean>;
    private hideCostOnProductionOrderPrint$?: Observable<boolean>;
    private orderTakerUsers$?: Observable<string>;
    private order3OfflineModeEnabled$?: Observable<boolean>;
    private useCashOnDelivery$?: Observable<boolean>;
    private realTimeStockItemBalanceUpdate$?: Observable<boolean>;
    private soLockSettings$?: Observable<SOLockSettings>;


    private isEdara2IntegrationConnected$?: Observable<boolean>;
    private edaraSetupCompleted$?: Observable<boolean>;
    private globalSettings$?: Observable<GlobalSettings>;
    private organizationProfile$?: Observable<OrganizationProfile>;
    private ordersSettings$?: Observable<OrdersSetting>;
    private purchaseInvoicesSettings$?: Observable<PurchaseInvoicesSetting>;
    private salesInvoicesSettings$?: Observable<SalesInvoicesSetting>;
    private productsSettings$?: Observable<ProductsSetting>;

    constructor(
        private httpClient: HttpClient,
        private authService: AuthService,
        private localDbProvider: DexieDbProvider) {
        this.baseUrl = AppSettingsService.appSettings.edaraCoreApi.baseUrl;
        this.authService.tenantSwitched$.subscribe({
            next: () => this.clearCache()
        });
    }

    public async offlineModeEnabled(): Promise<boolean> {
        if (!this.offlineModeEnabled$) {
            this.offlineModeEnabled$ = from(this.getSettingValue(SystemSettingsEnum.OfflineModeEnabled)).pipe(
                map(res => res ? (res.value === '1' || res.value === 'true') : false),
                shareReplay(1)
            );
        }
        return firstValueFrom(this.offlineModeEnabled$);
    }

    public async offlineModeExcludedUsers(): Promise<string> {
        if (!this.offlineModeExcludedUsers$) {
            this.offlineModeExcludedUsers$ = from(this.getSettingValue(SystemSettingsEnum.OfflineModeExcludedUsers)).pipe(
                map(res => res ? res.value : ''),
                shareReplay(1)
            );
        }
        return firstValueFrom(this.offlineModeExcludedUsers$);
    }

    public async hideSubTotal(): Promise<boolean> {
        if (!this.hideSubTotal$) {
            this.hideSubTotal$ = from(this.getSettingValue(SystemSettingsEnum.HideSubTotal)).pipe(
                map(res => res ? (res.value === '1' || res.value === 'true') : false),
                shareReplay(1)
            );
        }
        return firstValueFrom(this.hideSubTotal$);
    }

    public async systemStartDate(): Promise<string> {
        if (!this.systemStartDate$) {
            this.systemStartDate$ = from(this.getSettingValue(SystemSettingsEnum.SystemStartDate)).pipe(
                map(res => res ? moment(res.value, 'DD/MM/YYYY').toISOString() : ''),
                shareReplay(1)
            );
        }
        return firstValueFrom(this.systemStartDate$);
    }

    public async financialYearEndDate(): Promise<string> {
        if (!this.financialYearEndDate$) {
            this.financialYearEndDate$ = from(this.getSettingValue(SystemSettingsEnum.FinancialYearEndDate)).pipe(
                map(res => res ? res.value : ''),
                shareReplay(1)
            );
        }
        return firstValueFrom(this.financialYearEndDate$);
    }

    public async allowSellingBySerialNumber(): Promise<boolean> {
        if (!this.allowSellingBySerialNumber$) {
            this.allowSellingBySerialNumber$ = from(this.getSettingValue(SystemSettingsEnum.AllowSellingBySerialNumber)).pipe(
                map(res => res ? (res.value === '1' || res.value === 'true') : false),
                shareReplay(1)
            );
        }
        return firstValueFrom(this.allowSellingBySerialNumber$);
    }

    public async checkSerialOwner(): Promise<boolean> {
        if (!this.checkSerialOwner$) {
            this.checkSerialOwner$ = from(this.getSettingValue(SystemSettingsEnum.CheckSerialOwner)).pipe(
                map(res => res ? (res.value === '1' || res.value === 'true') : false),
                shareReplay(1)
            );
        }
        return firstValueFrom(this.checkSerialOwner$);
    }

    public async checkSerialAvailability(): Promise<boolean> {
        if (!this.checkSerialAvailability$) {
            this.checkSerialAvailability$ = from(this.getSettingValue(SystemSettingsEnum.CheckSerialAvailability)).pipe(
                map(res => res ? (res.value === '1' || res.value === 'true') : false),
                shareReplay(1)
            );
        }
        return firstValueFrom(this.checkSerialAvailability$);
    }

    public async checkSerialFulfillment(): Promise<boolean> {
        if (!this.checkSerialFulfillment$) {
            this.checkSerialFulfillment$ = from(this.getSettingValue(SystemSettingsEnum.CheckSerialFulfillment)).pipe(
                map(res => res ? (res.value === '1' || res.value === 'true') : false),
                shareReplay(1)
            );
        }
        return firstValueFrom(this.checkSerialFulfillment$);
    }

    public async inventoryManagementMethod(): Promise<InventoryManagementMethod> {
        if (!this.inventoryManagementMethod$) {
            this.inventoryManagementMethod$ = from(this.getSettingValue(SystemSettingsEnum.InventoryManagementMethod)).pipe(
                map(res => res ? res.value as InventoryManagementMethod : InventoryManagementMethod.None),
                shareReplay(1)
            );
        }
        return firstValueFrom(this.inventoryManagementMethod$);
    }

    public async hideBalanceAndBatchDetails(): Promise<boolean> {
        if (!this.hideBalanceAndBatchDetails$) {
            this.hideBalanceAndBatchDetails$ = from(this.getSettingValue(SystemSettingsEnum.HideBalanceAndBatchDetails)).pipe(
                map(res => res ? (res.value === '1' || res.value === 'true') : false),
                shareReplay(1)
            );
        }
        return firstValueFrom(this.hideBalanceAndBatchDetails$);
    }

    public async uiControlToFocusOnAfterAddingItem(): Promise<string> {
        if (!this.uiControlToFocusOnAfterAddingItem$) {
            this.uiControlToFocusOnAfterAddingItem$ = from(this.getSettingValue(SystemSettingsEnum.UiControlToFocusOnAfterAddingItem)).pipe(
                map(res => res ? res.value : UiControlToFocusOnAfterAddingItemEnum.SearchInput),
                shareReplay(1)
            );
        }
        return firstValueFrom(this.uiControlToFocusOnAfterAddingItem$);
    }

    public async prioritizesScanningBarcode(): Promise<PrioritizesScanningBarcodeEnum> {
        if (!this.prioritizesScanningBarcode$) {
            this.prioritizesScanningBarcode$ = from(this.getSettingValue(SystemSettingsEnum.PrioritizesScanningBarcode)).pipe(
                map(res => res ? +res.value : PrioritizesScanningBarcodeEnum.normalOverWeighted),
                shareReplay(1)
            );
        }
        return firstValueFrom(this.prioritizesScanningBarcode$);
    }

    public async preventSellingZeroBalanceStockItems(): Promise<boolean> {
        if (!this.preventSellingZeroBalanceStockItems$) {
            this.preventSellingZeroBalanceStockItems$ = from(this.getSettingValue(SystemSettingsEnum.PreventSellingZeroBalanceStockItems))
                .pipe(
                    map(res => res ? (res.value === '1' || res.value === 'true') : false),
                    shareReplay(1)
                );
        }
        return firstValueFrom(this.preventSellingZeroBalanceStockItems$);
    }

    public async hideCostOnProductionOrderPrint(): Promise<boolean> {
        if (!this.hideCostOnProductionOrderPrint$) {
            this.hideCostOnProductionOrderPrint$ = from(this.getSettingValue(SystemSettingsEnum.HideCostOnProductionOrderPrint))
                .pipe(
                    map(res => res ? (res.value === '1' || res.value === 'true') : false),
                    shareReplay(1)
                );
        }
        return firstValueFrom(this.hideCostOnProductionOrderPrint$);
    }

    public async orderTakerUsers(): Promise<string> {
        if (!this.orderTakerUsers$) {
            this.orderTakerUsers$ = from(this.getSettingValue(SystemSettingsEnum.OrderTakerUsers)).pipe(
                map(res => res ? res.value : ''),
                shareReplay(1)
            );
        }
        return firstValueFrom(this.orderTakerUsers$);
    }

    public async order3OfflineModeEnabled(): Promise<boolean> {
        if (!this.order3OfflineModeEnabled$) {
            this.order3OfflineModeEnabled$ = from(new Promise<boolean>((resolve) => resolve(true)));
        }
        return firstValueFrom(this.order3OfflineModeEnabled$);
    }

    public async useCashOnDelivery(): Promise<boolean> {
        if (!this.useCashOnDelivery$) {
            this.useCashOnDelivery$ = from(this.getSettingValue(SystemSettingsEnum.UseCashOnDelivery)).pipe(
                map(res => res ? (res.value === '1' || res.value === 'true') : false),
                shareReplay(1)
            );
        }
        return firstValueFrom(this.useCashOnDelivery$);
    }

    public async getSOLockSettings(): Promise<SOLockSettings> {
        if (!this.soLockSettings$) {
            this.soLockSettings$ = from(this.getSettingValue(SystemSettingsEnum.SOLockSettings)).pipe(
                map(res => {
                    if (!res || !res.value) {
                        return {
                            isLockEnabled: false,
                            lockType: SOLockTypes.NoAuto,
                            printTemplates: [],
                            automaticActions: SOLockAutomaticActions.NoAutomaticActions
                        } as SOLockSettings;
                    }

                    const settings = JSON.parse(res.value);
                    return {
                        isLockEnabled: settings.isLockEnabled ?? false,
                        lockType: settings.lockType ?? SOLockTypes.NoAuto,
                        printTemplates: Array.isArray(settings.printTemplates) ? settings.printTemplates : [],
                        automaticActions: settings.automaticActions ?? SOLockAutomaticActions.NoAutomaticActions
                    };
                }),
                shareReplay(1)
            );
        }
        return firstValueFrom(this.soLockSettings$);
    }


    public async realTimeStockItemBalanceUpdate(): Promise<boolean> {
        if (!this.realTimeStockItemBalanceUpdate$) {
            this.realTimeStockItemBalanceUpdate$ = from(this.getSettingValue(SystemSettingsEnum.RealTimeStockItemBalanceUpdate)).pipe(
                map(res => res ? (res.value === '1' || res.value === 'true') : false),
                shareReplay(1)
            );
        }
        return firstValueFrom(this.realTimeStockItemBalanceUpdate$);
    }

    public async defaultCurrency(): Promise<number> {
        if (!this.organizationProfile$) {
            this.organizationProfile$ = from(this.getSettingValue(SystemSettingsEnum.OrganizationProfile)).pipe(
                map(res => res ? OrganizationProfile.clone(JSON.parse(res.value)) : new OrganizationProfile()),
                shareReplay(1)
            );
        }
        return (await firstValueFrom(this.organizationProfile$)).currency;
    }

    public async IsEdara2IntegrationConnected(): Promise<boolean> {
        if (!this.isEdara2IntegrationConnected$) {
            this.isEdara2IntegrationConnected$ = from(this.getSettingValue(SystemSettingsEnum.IsEdara2IntegrationConnected)).pipe(
                map(res => res ? (res.value === '1' || res.value === 'true') : false),
                shareReplay(1)
            );
        }
        return firstValueFrom(this.isEdara2IntegrationConnected$);
    }

    public async EdaraSetupCompleted(): Promise<boolean> {
        if (!this.edaraSetupCompleted$) {
            this.edaraSetupCompleted$ = from(this.getSettingValue(SystemSettingsEnum.EdaraSetupCompleted)).pipe(
                map(res => res ? (res.value === '1' || res.value === 'true') : false),
                shareReplay(1)
            );
        }
        return firstValueFrom(this.edaraSetupCompleted$);
    }

    public async GlobalSettings(): Promise<GlobalSettings> {
        if (!this.globalSettings$) {
            this.globalSettings$ = from(this.getSettingValue(SystemSettingsEnum.Global)).pipe(
                map(res => res ? GlobalSettings.clone(JSON.parse(res.value)) : new GlobalSettings()),
                shareReplay(1)
            );
        }
        return firstValueFrom(this.globalSettings$);
    }

    public async isDataReplicationEnabled(): Promise<boolean> {
        try {
            const settings = await this.GlobalSettings();
            return settings?.desktopApp?.dataReplication === true;
        } catch (error) {
            console.warn('[SystemSettingsService] Could not read the Data replication setting; treating it as Off.', error);
            return false;
        }
    }

    public async OrganizationProfile(): Promise<OrganizationProfile> {
        if (!this.organizationProfile$) {
            this.organizationProfile$ = from(this.getSettingValue(SystemSettingsEnum.OrganizationProfile)).pipe(
                map(res => res ? OrganizationProfile.clone(JSON.parse(res.value)) : new OrganizationProfile()),
                shareReplay(1)
            );
        }
        return firstValueFrom(this.organizationProfile$);
    }

    public async OrdersSettings(): Promise<OrdersSetting> {
        if (!this.ordersSettings$) {
            this.ordersSettings$ = from(this.getSettingValue(SystemSettingsEnum.Order)).pipe(
                map(res => res ? OrdersSetting.clone(JSON.parse(res.value)) : new OrdersSetting()),
                shareReplay(1)
            );
        }
        return firstValueFrom(this.ordersSettings$);
    }

    public async PurchaseInvoicesSettings(): Promise<PurchaseInvoicesSetting> {
        if (!this.purchaseInvoicesSettings$) {
            this.purchaseInvoicesSettings$ = from(this.getSettingValue(SystemSettingsEnum.PurchaseInvoice)).pipe(
                map(res => res ? PurchaseInvoicesSetting.clone(JSON.parse(res.value)) : new PurchaseInvoicesSetting()),
                shareReplay(1)
            );
        }
        return firstValueFrom(this.purchaseInvoicesSettings$);
    }

    public async SalesInvoicesSettings(): Promise<SalesInvoicesSetting> {
        if (!this.salesInvoicesSettings$) {
            this.salesInvoicesSettings$ = from(this.getSettingValue(SystemSettingsEnum.SalesInvoice)).pipe(
                map(res => res ? SalesInvoicesSetting.clone(JSON.parse(res.value)) : new SalesInvoicesSetting()),
                shareReplay(1)
            );
        }
        return firstValueFrom(this.salesInvoicesSettings$);
    }

    public async ProductsSettings(): Promise<ProductsSetting> {
        if (!this.productsSettings$) {
            this.productsSettings$ = from(this.getSettingValue(SystemSettingsEnum.Product)).pipe(
                map(res => res ? ProductsSetting.clone(JSON.parse(res.value)) : new ProductsSetting()),
                shareReplay(1)
            );
        }
        return firstValueFrom(this.productsSettings$);
    }

    private async getSettingValue(name: string): Promise<SystemSetting | undefined> {
        const setting = await this.getSystemSettingByNameLocalAsync(name);
        if (setting) {
            return setting;
        } else {
            const response = await this.getSystemSettingByNameAync(name);
            this.saveSystemSettingLocal(response);
            return response;
        }
    }

    clearCache() {
        this.offlineModeEnabled$ = undefined;
        this.offlineModeExcludedUsers$ = undefined;
        this.hideSubTotal$ = undefined;
        this.systemStartDate$ = undefined;
        this.financialYearEndDate$ = undefined;
        this.allowSellingBySerialNumber$ = undefined;
        this.checkSerialOwner$ = undefined;
        this.checkSerialAvailability$ = undefined;
        this.checkSerialFulfillment$ = undefined;
        this.inventoryManagementMethod$ = undefined;
        this.hideBalanceAndBatchDetails$ = undefined;
        this.uiControlToFocusOnAfterAddingItem$ = undefined;
        this.prioritizesScanningBarcode$ = undefined;
        this.preventSellingZeroBalanceStockItems$ = undefined;
        this.hideCostOnProductionOrderPrint$ = undefined;
        this.orderTakerUsers$ = undefined;
        this.order3OfflineModeEnabled$ = undefined;
        this.useCashOnDelivery$ = undefined;
        this.realTimeStockItemBalanceUpdate$ = undefined;
        this.soLockSettings$ = undefined;

        this.isEdara2IntegrationConnected$ = undefined;
        this.edaraSetupCompleted$ = undefined;
        this.globalSettings$ = undefined;
        this.organizationProfile$ = undefined;
        this.ordersSettings$ = undefined;
        this.purchaseInvoicesSettings$ = undefined;
        this.salesInvoicesSettings$ = undefined;
        this.productsSettings$ = undefined;
    }

    //#region System Settings

    findSystemSettingsAsync(query: any): Promise<PagedList<SystemSetting[]> | undefined> {
        const queryString = new QueryStringHelper().toQueryString(query);
        return firstValueFrom(
            this.httpClient.get<PagedList<SystemSetting[]>>(this.baseUrl + 'api/systemsettings/find?' + queryString)
        ).catch(() => { return undefined; });
    }

    getSystemSettingByName(name: string): Observable<SystemSetting> {
        return this.httpClient.get<SystemSetting>(this.baseUrl + 'api/systemsettings/FindByName/' + name);
    }

    getSystemSettingByNameAync(name: string): Promise<SystemSetting | undefined> {
        return firstValueFrom(
            this.httpClient.get<SystemSetting>(this.baseUrl + 'api/systemsettings/FindByName/' + name)
        ).catch(() => { return undefined; });
    }

    getModulesBasedOnPricingPlan(): Promise<SystemSetting | undefined> {
        return firstValueFrom(
            this.httpClient.get<SystemSetting>(this.baseUrl + 'api/SystemSettings/GetModulesBasedOnPricingPlan')
        ).catch(() => { return undefined; });
    }

    saveSystemSetting(systemSetting: SystemSetting): Observable<SystemSetting> {
        return this.httpClient.put<SystemSetting>(this.baseUrl + 'api/systemsettings/SaveOrUpdate', systemSetting);
    }

    saveSystemSettingAsync(systemSetting: SystemSetting): Promise<SystemSetting | undefined> {
        return firstValueFrom(
            this.httpClient.put<SystemSetting>(this.baseUrl + 'api/systemsettings/SaveOrUpdate', systemSetting)
        ).catch(() => { return undefined; });
    }

    getSystemSettingByNameLocalAsync(name: string): Promise<SystemSetting> {
        return this.localDbProvider.db.systemSettings
            .where({ name: name })
            .first((element: SystemSetting) => {
                return element;
            });
    }

    addSystemSettingsToLocalDb(settings: SystemSetting[]) {
        settings.forEach(element => {
            this.saveSystemSettingLocal(element);
        });
    }

    saveSystemSettingLocal(systemSetting?: SystemSetting) {
        if (systemSetting) {
            this.localDbProvider.db.systemSettings.put(systemSetting)
                .catch((err: any) => this.localDbProvider.handleErrors(err));
        }
    }

    deleteSystemSettingAsync(id: string): Promise<boolean> {
        return firstValueFrom(
            this.httpClient.delete<boolean>(this.baseUrl + 'api/systemsettings/' + id)
        ).catch(() => { return false; });
    }

    deleteSystemSettingLocal(id: string) {
        if (id) {
            this.localDbProvider.db.systemSettings.where({ id: id }).delete()
                .catch((err: any) => this.localDbProvider.handleErrors(err));
        }
    }

    //#endregion

    connectZatca(otp: string): Observable<EInvoiceKsaConfiguration> {
        return this.httpClient.get<EInvoiceKsaConfiguration>(this.baseUrl + 'api/SystemSettings/GetKSAEInvoiceConfiguration', {
            params: { OTP: otp }
        });
    }
}
